import React, { memo, useCallback } from 'react';
import { useFocusEffect } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  AnimatedViews,
  BodyText,
  Button,
  HStack,
  IllusEmployeeBag,
  Image,
  theme,
  useResponsive,
} from '@sg/garnish';

import {
  AccountFavoritesLoading,
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
  SharedLineItem,
} from '../../components';
import { type PartialLineItem } from '../../graphql';
import { useStartReorderLineItem } from '../ReorderingScreen/hooks';
import { useAccountFavorites } from './hooks';

export const FavoritesScreen = (): React.ReactElement => {
  const { formatMessage } = useIntl();
  const {
    favorites,
    canFetchMoreFavorites,
    isFetchingInitialFavorites,
    isFetchingMoreFavorites,
    isTogglingFavoriteState,
    fetchInitialFavorites,
    fetchMoreFavorites,
    toggleFavoriteLineItem,
  } = useAccountFavorites();
  const startReorderLineItem = useStartReorderLineItem();
  const itemsPerRow = useItemsPerRow();

  const containerA11ySelector = useA11ySelector('account.favorites.container');

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isFavoriteToggleDisabled = isTogglingFavoriteState || isFetchingMoreFavorites;

  // ─── Effects ───────────────────────────────────────────────────────────────

  useFocusEffect(fetchInitialFavorites);

  // ─── Styles ──────────────────────────────────────────────────────────

  const { match } = useResponsive();
  const screenSectionStyles = match([undefined, styles.screenSectionSM]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="favorites" {...containerA11ySelector.props}>
      <AccountScreenTitle title={formatMessage(messages.accountFavoritesTitle)} />
      <AccountScreenSection style={screenSectionStyles}>
        {isFetchingInitialFavorites ? (
          <AccountFavoritesLoading itemsPerRow={itemsPerRow} />
        ) : (
          <FavoritesScreenContent
            favorites={favorites}
            canLoadMore={canFetchMoreFavorites}
            isLoadingMore={isFetchingMoreFavorites}
            isFavoriteToggleDisabled={isFavoriteToggleDisabled}
            handleLoadMore={fetchMoreFavorites}
            startReorderLineItem={startReorderLineItem}
            toggleFavorite={toggleFavoriteLineItem}
          />
        )}
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

export const FavoritesScreenContent = (props: FavoritesScreenContentProps) => {
  const {
    favorites,
    canLoadMore,
    isLoadingMore,
    isFavoriteToggleDisabled,
    handleLoadMore,
    startReorderLineItem,
    toggleFavorite,
  } = props;

  const { formatMessage } = useIntl();
  const itemsPerRow = useItemsPerRow();

  if (favorites.length === 0) {
    return (
      <AnimatedViews.Reveal style={styles.revealContainer}>
        <EmptyState />
      </AnimatedViews.Reveal>
    );
  }

  return (
    <View style={styles.container}>
      <HStack itemsPerRow={itemsPerRow} style={styles.favoritesStack}>
        {favorites.map((favorite, index) => (
          <AnimatedViews.Reveal
            key={favorite.id}
            delay={getRevealDelay(index)}
            style={styles.revealContainer}
          >
            <ConnectedFavorite
              favorite={favorite}
              isFavoriteToggleDisabled={isFavoriteToggleDisabled}
              startReorderLineItem={startReorderLineItem}
              toggleFavorite={toggleFavorite}
            />
          </AnimatedViews.Reveal>
        ))}
      </HStack>

      {favorites.length > 0 && canLoadMore ? (
        <Button
          testID="account.favorites.load-more"
          palette="secondary"
          accessibilityRole="button"
          accessibilityLabel={formatMessage(messages.accountFavoritesLoadMore)}
          accessibilityHint={formatMessage(messages.accountFavoritesLoadMore)}
          style={styles.loadMoreButton}
          isLoading={isLoadingMore}
          onPress={handleLoadMore}
        >
          {formatMessage(messages.accountFavoritesLoadMore)}
        </Button>
      ) : null}
    </View>
  );
};

const ConnectedFavorite = memo((props: ConnectedFavoriteProps) => {
  const { favorite, isFavoriteToggleDisabled, startReorderLineItem, toggleFavorite } = props;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onAddToBag = useCallback(async () => {
    await startReorderLineItem(favorite as PartialLineItem);
  }, [favorite, startReorderLineItem]);

  const onFavorite = useCallback(async () => {
    toggleFavorite({
      id: favorite.id,
      productId: favorite.product.id,
      isFavorite: !favorite.favorited,
    });
  }, [favorite, toggleFavorite]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SharedLineItem
      lineItem={favorite as PartialLineItem}
      isFavoriteToggleDisabled={isFavoriteToggleDisabled}
      onAddToBag={onAddToBag}
      onFavorite={onFavorite}
    />
  );
});

const EmptyState = () => {
  const { formatMessage } = useIntl();

  return (
    <View style={styles.emptyContainer}>
      <Image
        source={IllusEmployeeBag}
        style={styles.emptyImage}
        aria-label={formatMessage(messages.accountFavoritesIllustrationAlt)}
      />
      <BodyText style={styles.noResultsText}>
        {formatMessage(messages.accountFavoritesEmpty)}
      </BodyText>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Staggers the first few cards only, so cards from later pages appear right away.
 */
function getRevealDelay(index: number) {
  return Math.min(index, MAX_STAGGERED_CARDS) * CARD_REVEAL_STAGGER;
}

const useItemsPerRow = () => {
  const { match } = useResponsive();

  return match([1, 1, 2]);
};

const styles = StyleSheet.create({
  revealContainer: {
    flexGrow: 1,
  },
  container: {
    paddingBottom: theme.spacing['6'],
  },
  screenSectionSM: {
    maxWidth: 800,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyImage: {
    width: 220,
    height: 220,
    marginBottom: theme.spacing['4'],
  },
  noResultsText: {
    textAlign: 'center',
  },
  loadMoreButton: {
    minWidth: 200,
    maxWidth: 200,
    marginHorizontal: 'auto',
    marginTop: theme.spacing['6'],
  },
  favoritesStack: {
    marginTop: theme.spacing['4'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountFavoritesTitle: {
    defaultMessage: 'Favorites',
    description: 'Account | Favorites | Title',
  },
  accountFavoritesLoadMore: {
    defaultMessage: 'See more favorites',
    description: 'Account | Favorites | Load More',
  },
  accountFavoritesIllustrationAlt: {
    defaultMessage: 'Sweetgreen customer with a bag',
    description: 'Account | Favorites | Illustration | Alt',
  },
  accountFavoritesEmpty: {
    defaultMessage: 'Once you favorite a product your favorites will appear here',
    description: 'Account | Favorites | Empty',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_REVEAL_STAGGER = 65;
const MAX_STAGGERED_CARDS = 4;

// ─── Types ───────────────────────────────────────────────────────────────────

type FavoritesScreenContentProps = {
  favorites: AccountFavorites['favorites'];
  canLoadMore: boolean;
  isLoadingMore: boolean;
  isFavoriteToggleDisabled: boolean;
  handleLoadMore: () => void;
  startReorderLineItem: StartReorderLineItem;
  toggleFavorite: AccountFavorites['toggleFavoriteLineItem'];
};

type ConnectedFavoriteProps = {
  favorite: AccountFavorites['favorites'][number];
  isFavoriteToggleDisabled: boolean;
  startReorderLineItem: StartReorderLineItem;
  toggleFavorite: AccountFavorites['toggleFavoriteLineItem'];
};

type AccountFavorites = ReturnType<typeof useAccountFavorites>;

type StartReorderLineItem = ReturnType<typeof useStartReorderLineItem>;
